import { useState, useMemo, useEffect } from 'react';
import React from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { useAuth } from '@/contexts/AuthContext';
import { UserManagement } from '@/components/UserManagement';
import { AdminNavigation } from '@/components/AdminNavigation';
import { formatPrice, calculateSavingsPercentage } from '@/lib/utils';
import { 
  Eye, EyeOff, Grid, List, Search, SortAsc, SortDesc, 
  DollarSign, TrendingUp, Package, Calendar, Filter,
  Edit2, Trash2, Save, X, Upload, Plus, Download, Settings, Clock,
  Utensils, ShoppingCart, BookOpen, BarChart3
} from 'lucide-react';
import { EditProductModal } from '@/components/edit-product-modal';
import { AddProductModal } from '@/components/add-product-modal';
import { CSVImportExport } from '@/components/csv-import-export';
import { AdvancedProductSearch, type AdvancedSearchFilters } from '@/components/AdvancedProductSearch';
import { ProductAnalytics } from '@/components/ProductAnalytics';
import type { Product, InsertProduct, UpdateProduct, SystemSettings } from '@shared/schema';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { getCategoryColor, getCategoryLabel } from '@/lib/category-colors';
import { Link } from 'wouter';

type SortField = 'description' | 'retailPrice' | 'ourPrice' | 'savings' | 'category' | 'manual';
type SortOrder = 'asc' | 'desc';
type ViewMode = 'grid' | 'list';

// Product card component
function ProductCard({ product, onEdit, onDelete, onToggleActive, onToggleInstoreFlyer, isSelected, onSelectionChange }: {
  product: Product;
  onEdit: (product: Product) => void;
  onDelete: (id: number, name: string) => void;
  onToggleActive: (id: number, active: boolean) => void;
  onToggleInstoreFlyer: (id: number, instoreFlyer: boolean) => void;
}) {
  const [imageError, setImageError] = useState(false);
  
  return (
    <motion.div
      key={product.id}
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      whileHover={{ y: -4 }}
      className="h-full"
    >
      <Card className={`h-full hover:shadow-lg transition-shadow border-[1.5px] border-gray-400 ${!product.active ? 'opacity-50' : ''}`}>
        <CardContent className="p-4">
          {/* Selection and Category */}
          <div className="flex justify-between items-start mb-3">
            <Badge className={getCategoryColor(product.category, true)}>
              {getCategoryLabel(product.category)}
            </Badge>
            <div className="flex items-center gap-2">
              {/* Weekly Flyer Toggle */}
              <div className="flex items-center gap-1" title="Show on Weekly Flyer">
                <span className="text-xs text-orange-600 font-medium">Weekly</span>
                <Switch
                  checked={product.instoreFlyer}
                  onCheckedChange={(checked) => onToggleInstoreFlyer(product.id, checked)}
                  className="data-[state=checked]:bg-orange-500"
                />
              </div>
              {/* Active Toggle */}
              <Switch
                checked={product.active}
                onCheckedChange={(checked) => onToggleActive(product.id, checked)}
              />
            </div>
          </div>

          {/* Product Image */}
          <div className="w-full h-32 bg-gray-100 rounded-lg mb-3 overflow-hidden">
            {product.imageUrl && !imageError ? (
              <img 
                src={product.imageUrl} 
                alt={product.description}
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Package className="w-12 h-12 text-gray-400" />
              </div>
            )}
          </div>

          {/* Product Info */}
          <h3 className="font-semibold text-lg mb-1">{product.description}</h3>
          {product.chineseName && (
            <p className="text-gray-700 text-sm mb-1">{product.chineseName}</p>
          )}
          <p className="text-gray-600 text-sm mb-3">{product.size}</p>

          {/* Pricing */}
          <div className="space-y-2 mb-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-500">Retail:</span>
              <span className="font-medium line-through text-red-500">
                {formatPrice(product.retailPrice)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-500">Our Price:</span>
              <span className="font-bold text-emerald-600 text-xl">
                {formatPrice(product.ourPrice)}
              </span>
            </div>
            <div className="text-center">
              <Badge className="bg-emerald-100 text-emerald-800">
                Save {calculateSavingsPercentage(product.retailPrice, product.ourPrice)}%
              </Badge>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-1">
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1 h-7 px-2"
              onClick={() => onEdit(product)}
            >
              <Edit2 className="h-3 w-3" />
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1 h-7 px-2 text-red-600 hover:bg-red-50"
              onClick={() => onDelete(product.id, product.description)}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export default function AdminNew() {
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [sortField, setSortField] = useState<SortField>('manual');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isEditingTimestamp, setIsEditingTimestamp] = useState(false);
  const [timestampValue, setTimestampValue] = useState('');
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [productToDelete, setProductToDelete] = useState<{ id: number; name: string } | null>(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  
  // Advanced search filters
  const [searchFilters, setSearchFilters] = useState<AdvancedSearchFilters>({
    searchQuery: '',
    categoryFilter: 'all',
    priceMin: '',
    priceMax: '',
    statusFilter: 'all',
    savingsMin: '',
    savingsMax: ''
  });
  
  const { toast } = useToast();
  const { user, logout, isAdmin, isAuthenticated } = useAuth();


  // Logout function
  const handleLogout = () => {
    logout();
    toast({ title: "Logged out successfully" });
  };

  // Fetch products (including inactive for admin view)
  const { data: products = [], isLoading, error: productsError } = useQuery<Product[]>({
    queryKey: ['/api/products?includeInactive=true'],
    enabled: isAuthenticated(),
  });

  // Fetch system settings
  const { data: settings, error: settingsError } = useQuery<SystemSettings>({
    queryKey: ['/api/admin/settings'],
    enabled: isAuthenticated(),
  });

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: async (updates: { lastUpdated: Date }) => {
      return await apiRequest('PATCH', '/api/admin/settings', updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/settings'] });
      setIsEditingTimestamp(false);
      toast({ title: "Settings Updated" });
    },
    onError: (error) => {
      toast({
        title: "Update Failed",
        description: error instanceof Error ? error.message : "Failed to update settings",
        variant: "destructive",
      });
    },
  });

  // Set timestamp value when settings load
  useEffect(() => {
    if (settings?.lastUpdated) {
      const date = new Date(settings.lastUpdated);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      setTimestampValue(`${year}-${month}-${day}T${hours}:${minutes}`);
    }
  }, [settings]);

  // Filter and sort products with advanced search
  const processedProducts = useMemo(() => {
    let filtered = products;

    // Apply advanced search filters
    if (searchFilters.searchQuery) {
      const query = searchFilters.searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.description.toLowerCase().includes(query) ||
        p.size.toLowerCase().includes(query) ||
        (p.chineseName && p.chineseName.toLowerCase().includes(query))
      );
    }

    // Apply category filter
    if (searchFilters.categoryFilter !== 'all') {
      filtered = filtered.filter(p => p.category === searchFilters.categoryFilter);
    }

    // Apply status filter
    if (searchFilters.statusFilter !== 'all') {
      filtered = filtered.filter(p => 
        searchFilters.statusFilter === 'active' ? p.active : !p.active
      );
    }

    // Apply price range filter
    if (searchFilters.priceMin) {
      const minPrice = parseFloat(searchFilters.priceMin);
      if (!isNaN(minPrice)) {
        filtered = filtered.filter(p => p.ourPrice >= minPrice);
      }
    }
    if (searchFilters.priceMax) {
      const maxPrice = parseFloat(searchFilters.priceMax);
      if (!isNaN(maxPrice)) {
        filtered = filtered.filter(p => p.ourPrice <= maxPrice);
      }
    }

    // Apply savings range filter
    if (searchFilters.savingsMin || searchFilters.savingsMax) {
      filtered = filtered.filter(p => {
        const savings = calculateSavingsPercentage(p.retailPrice, p.ourPrice);
        const minSavings = searchFilters.savingsMin ? parseFloat(searchFilters.savingsMin) : 0;
        const maxSavings = searchFilters.savingsMax ? parseFloat(searchFilters.savingsMax) : 100;
        return savings >= minSavings && savings <= maxSavings;
      });
    }

    // Sort products
    if (sortField === 'manual') {
      // Products are already sorted by displayOrder from the server
      return filtered;
    }

    const sorted = [...filtered].sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortField) {
        case 'description':
          aValue = a.description.toLowerCase();
          bValue = b.description.toLowerCase();
          break;
        case 'retailPrice':
          aValue = a.retailPrice;
          bValue = b.retailPrice;
          break;
        case 'ourPrice':
          aValue = a.ourPrice;
          bValue = b.ourPrice;
          break;
        case 'savings':
          aValue = calculateSavingsPercentage(a.retailPrice, a.ourPrice);
          bValue = calculateSavingsPercentage(b.retailPrice, b.ourPrice);
          break;
        case 'category':
          aValue = a.category;
          bValue = b.category;
          break;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return sorted;
  }, [products, searchFilters, sortField, sortOrder]);

  // Group products by category
  const productsByCategory = useMemo(() => {
    const grouped: Record<string, Product[]> = {
      produce: [],
      'meat-beef': [],
      'meat-chicken': [],
      'meat-pork': [],
      'meat-hotpot': [],
      seafood: [],
      groceries: []
    };
    
    processedProducts.forEach(product => {
      if (grouped[product.category]) {
        grouped[product.category].push(product);
      }
    });
    
    return grouped;
  }, [processedProducts]);

  // Stats
  const stats = useMemo(() => {
    const active = products.filter(p => p.active).length;
    const totalRetail = products.reduce((sum, p) => sum + p.retailPrice, 0);
    const totalOur = products.reduce((sum, p) => sum + p.ourPrice, 0);
    const avgSavings = products.length > 0 
      ? Math.round(((totalRetail - totalOur) / totalRetail) * 100)
      : 0;

    return {
      total: products.length,
      active,
      avgSavings
    };
  }, [products]);

  // Update product mutation
  const updateProductMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: number; updates: UpdateProduct }) => {
      return await apiRequest('PATCH', `/api/admin/products/${id}`, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/products?includeInactive=true'] });
      setEditingProduct(null);
      toast({ title: "Product Updated" });
    },
    onError: (error) => {
      toast({
        title: "Update Failed",
        description: error instanceof Error ? error.message : "Failed to update product",
        variant: "destructive",
      });
    },
  });

  // Delete product mutation
  const deleteProductMutation = useMutation({
    mutationFn: async (id: number) => {
      return await apiRequest('DELETE', `/api/admin/products/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/products?includeInactive=true'] });
      toast({ title: "Product Deleted" });
      setProductToDelete(null);
    },
    onError: (error) => {
      toast({
        title: "Delete Failed",
        description: error instanceof Error ? error.message : "Failed to delete product",
        variant: "destructive",
      });
      setProductToDelete(null);
    },
  });

  // Handle delete confirmation
  const handleDeleteConfirm = () => {
    if (productToDelete) {
      deleteProductMutation.mutate(productToDelete.id);
    }
  };

  // Create product mutation
  const createProductMutation = useMutation({
    mutationFn: async (product: InsertProduct) => {
      return await apiRequest('POST', '/api/admin/products', product);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/products?includeInactive=true'] });
      setShowAddForm(false);
      toast({ title: "Product Created" });
    },
    onError: (error) => {
      toast({
        title: "Create Failed",
        description: error instanceof Error ? error.message : "Failed to create product",
        variant: "destructive",
      });
    },
  });

  // Reorder products mutation
  const reorderProductsMutation = useMutation({
    mutationFn: async (productIds: number[]) => {
      return await apiRequest('POST', '/api/admin/products/reorder', { productIds });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/products?includeInactive=true'] });
      toast({ title: "Products Reordered" });
    },
    onError: (error) => {
      toast({
        title: "Reorder Failed",
        description: error instanceof Error ? error.message : "Failed to reorder products",
        variant: "destructive",
      });
    },
  });

  // Login is now handled by AuthContext

  const handleSaveTimestamp = () => {
    if (!timestampValue) {
      toast({
        title: "Invalid Date",
        description: "Please enter a valid date and time",
        variant: "destructive",
      });
      return;
    }
    const date = new Date(timestampValue);
    if (isNaN(date.getTime())) {
      toast({
        title: "Invalid Date",
        description: "Please enter a valid date and time",
        variant: "destructive",
      });
      return;
    }
    updateSettingsMutation.mutate({ lastUpdated: date });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleMoveProduct = (productId: number, direction: 'up' | 'down') => {
    const currentIndex = processedProducts.findIndex(p => p.id === productId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= processedProducts.length) return;
    
    const reorderedProducts = [...processedProducts];
    const [movedProduct] = reorderedProducts.splice(currentIndex, 1);
    reorderedProducts.splice(newIndex, 0, movedProduct);
    
    const productIds = reorderedProducts.map(p => p.id);
    reorderProductsMutation.mutate(productIds);
  };

  const canReorder = sortField === 'manual' && 
    searchFilters.searchQuery === '' && 
    searchFilters.categoryFilter === 'all' && 
    searchFilters.statusFilter === 'all' &&
    !searchFilters.priceMin && !searchFilters.priceMax &&
    !searchFilters.savingsMin && !searchFilters.savingsMax;

  // Get unique categories for advanced search
  const categories = useMemo(() => {
    return [...new Set(products.map(p => p.category))];
  }, [products]);



  // Authentication is handled by ProtectedRoute wrapper

  // Add error display if products fail (settings error is ok - may be non-superadmin)
  if (productsError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-red-600 mb-2">Error Loading Data</h2>
            <p className="text-gray-600">
              Products: {productsError.message}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card shadow-sm border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <img 
                src="/images/TopsLogoCrop.png" 
                alt="Tops Supermarket" 
                className="h-10"
              />
              <h1 className="text-3xl font-bold text-foreground">Product Management</h1>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-100 text-emerald-800">
                  {settings?.status === 'live' ? 'System Live' : 'System Offline'}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {user?.username} ({user?.role})
                </span>
              </div>
              <div className="flex items-center gap-2">
                {isAdmin() && (
                  <Button variant="outline" onClick={() => setShowUserManagement(true)}>
                    <Settings className="h-4 w-4 mr-2" />
                    Users
                  </Button>
                )}
                <Button variant="outline" onClick={handleLogout}>
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <AdminNavigation />

      {/* Stats Cards */}
      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <motion.div whileHover={{ scale: 1.02 }}>
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100">Total Products</p>
                    <p className="text-3xl font-bold">{stats.total}</p>
                  </div>
                  <Package className="h-8 w-8 text-blue-200" />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div whileHover={{ scale: 1.02 }}>
            <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100">Active Products</p>
                    <p className="text-3xl font-bold">{stats.active}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-green-200" />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div whileHover={{ scale: 1.02 }}>
            <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100">Avg Savings</p>
                    <p className="text-3xl font-bold">{stats.avgSavings}%</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-purple-200" />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* System Settings - More Prominent */}
        <Card className="mb-6 border-2 border-amber-500 bg-gradient-to-r from-amber-50 to-amber-100 animate-[glow_2s_ease-in-out_infinite]">
          <CardHeader className="bg-amber-300 border-b border-amber-500">
            <CardTitle className="flex items-center gap-2 text-xl">
              <Settings className="h-6 w-6 text-black" />
              <span className="text-black">Live Price Timestamp - Change frequently</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-green-700 animate-pulse" />
                <span className="font-bold text-lg text-gray-700">LIVE PRICES as of:</span>
              </div>
              
              {isEditingTimestamp ? (
                <div className="flex items-center gap-2 flex-1">
                  <Input
                    type="datetime-local"
                    value={timestampValue}
                    onChange={(e) => setTimestampValue(e.target.value)}
                    className="max-w-xs"
                  />
                  <Button 
                    size="sm" 
                    onClick={handleSaveTimestamp}
                    disabled={updateSettingsMutation.isPending}
                  >
                    <Save className="h-4 w-4 mr-1" />
                    Save
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      setIsEditingTimestamp(false);
                      if (settings?.lastUpdated) {
                        const date = new Date(settings.lastUpdated);
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        const hours = String(date.getHours()).padStart(2, '0');
                        const minutes = String(date.getMinutes()).padStart(2, '0');
                        setTimestampValue(`${year}-${month}-${day}T${hours}:${minutes}`);
                      }
                    }}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <span className="text-lg sm:text-2xl font-black text-green-100 bg-green-800 px-3 sm:px-4 py-2 rounded-lg border-2 border-green-700 break-all">
                    {settings?.lastUpdated ? new Date(settings.lastUpdated).toLocaleString('en-US', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false
                    }) : 'Not set'}
                  </span>
                  <Button 
                    size="default" 
                    variant="default"
                    onClick={() => setIsEditingTimestamp(true)}
                    className="font-bold w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Edit2 className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">Update Timestamp</span>
                    <span className="sm:hidden">Update</span>
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Advanced Search */}
        <AdvancedProductSearch
          filters={searchFilters}
          onFiltersChange={setSearchFilters}
          categories={categories}
          totalResults={processedProducts.length}
        />


        {/* Analytics */}
        {showAnalytics && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Product Analytics & Insights</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAnalytics(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ProductAnalytics products={processedProducts} />
            </CardContent>
          </Card>
        )}

        {/* Controls Bar */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
              {/* Sort Options */}
              <div className="flex flex-wrap sm:flex-nowrap items-center gap-2">
                <Select value={sortField} onValueChange={(value: SortField) => setSortField(value)}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual Order</SelectItem>
                    <SelectItem value="description">Name</SelectItem>
                    <SelectItem value="retailPrice">Retail Price</SelectItem>
                    <SelectItem value="ourPrice">Our Price</SelectItem>
                    <SelectItem value="savings">Savings %</SelectItem>
                    <SelectItem value="category">Category</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                >
                  {sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />}
                </Button>
              </div>

              {/* View Mode and Actions */}
              <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-start">
                <div className="flex gap-1">
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'outline'}
                    size="icon"
                    onClick={() => setViewMode('grid')}
                  >
                    <Grid className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'outline'}
                    size="icon"
                    onClick={() => setViewMode('list')}
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>

                {/* Analytics Toggle */}
                <Button
                  variant={showAnalytics ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setShowAnalytics(!showAnalytics)}
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Analytics
                </Button>

                {/* Add Product */}
                <Button onClick={() => setShowAddForm(true)} className="whitespace-nowrap">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Product
                </Button>
              </div>
            </div>

            {/* CSV Import/Export */}
            <div className="mt-4 pt-4 border-t">
              <CSVImportExport 
                onImportComplete={() => queryClient.invalidateQueries({ queryKey: ['admin-products'] })}
                includeInactive={true}
              />
            </div>

            {/* Reorder Info */}
            {canReorder && (
              <div className="mt-2 text-sm text-gray-600">
                Use arrow buttons to reorder products
              </div>
            )}
            {sortField === 'manual' && (!canReorder) && (
              <div className="mt-2 text-sm text-amber-600">
                Clear search and filters to enable manual reordering
              </div>
            )}
          </CardContent>
        </Card>

        {/* Products Display */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center gap-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
              <span className="text-gray-600">Loading products...</span>
            </div>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="space-y-6">
            {Object.entries(productsByCategory).map(([category, categoryProducts]) => (
              categoryProducts.length > 0 && (
                <div key={category} className={`p-4 rounded-lg mb-6 ${
                  category === 'produce' ? 'bg-green-50/50 border-2 border-green-200/50' :
                  category.startsWith('meat') ? 'bg-red-50/50 border-2 border-red-200/50' :
                  category === 'seafood' ? 'bg-blue-50/50 border-2 border-blue-200/50' :
                  category === 'groceries' ? 'bg-yellow-50/50 border-2 border-yellow-200/50' :
                  'bg-gray-50/50 border-2 border-gray-200/50'
                }`}>
                  <h2 className={`text-xl font-semibold mb-3 ${
                    category === 'produce' ? 'text-green-800' :
                    category.startsWith('meat') ? 'text-red-800' :
                    category === 'seafood' ? 'text-blue-800' :
                    category === 'groceries' ? 'text-amber-800' :
                    'text-gray-800'
                  }`}>
                    {category === 'meat-beef' ? 'Meat - Beef' :
                     category === 'meat-chicken' ? 'Meat - Chicken' :
                     category === 'meat-pork' ? 'Meat - Pork' :
                     category === 'meat-hotpot' ? 'Meat - Hot Pot' :
                     category === 'produce' ? 'Produce' :
                     category === 'seafood' ? 'Seafood' :
                     category === 'groceries' ? 'Groceries' :
                     category}
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3">
                    <AnimatePresence>
                      {categoryProducts.map((product) => (
                        <ProductCard
                          key={product.id}
                          product={product}
                          onEdit={setEditingProduct}
                          onDelete={(id, name) => setProductToDelete({ id, name })}
                          onToggleActive={(id, active) => updateProductMutation.mutate({ id, updates: { active } })}
                          onToggleInstoreFlyer={(id, instoreFlyer) => updateProductMutation.mutate({ id, updates: { instoreFlyer } })}
                        />
                      ))}
                    </AnimatePresence>
                  </div>
                </div>
              )
            ))}
          </div>
        ) : (
          <Card className="border-4 border-black">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b-4 border-black">
                    <tr>
                      {canReorder && <th className="px-4 py-3 text-left w-24">Order</th>}
                      <th className="px-4 py-3 text-left">
                        <button 
                          className="flex items-center gap-1 font-semibold hover:text-emerald-600"
                          onClick={() => handleSort('description')}
                        >
                          Product
                          {sortField === 'description' && (
                            sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />
                          )}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left">
                        <button 
                          className="flex items-center gap-1 font-semibold hover:text-emerald-600"
                          onClick={() => handleSort('category')}
                        >
                          Category
                          {sortField === 'category' && (
                            sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />
                          )}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left">Size</th>
                      <th className="px-4 py-3 text-left">
                        <button 
                          className="flex items-center gap-1 font-semibold hover:text-emerald-600"
                          onClick={() => handleSort('retailPrice')}
                        >
                          Retail
                          {sortField === 'retailPrice' && (
                            sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />
                          )}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left">
                        <button 
                          className="flex items-center gap-1 font-semibold hover:text-emerald-600"
                          onClick={() => handleSort('ourPrice')}
                        >
                          Our Price
                          {sortField === 'ourPrice' && (
                            sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />
                          )}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left">
                        <button 
                          className="flex items-center gap-1 font-semibold hover:text-emerald-600"
                          onClick={() => handleSort('savings')}
                        >
                          Savings
                          {sortField === 'savings' && (
                            sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />
                          )}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <AnimatePresence>
                      {Object.entries(productsByCategory).map(([category, categoryProducts]) => (
                        categoryProducts.length > 0 && (
                          <React.Fragment key={category}>
                            <tr className="border-y-2 border-black bg-gray-800">
                              <td colSpan={canReorder ? 11 : 10} className="px-4 py-2 font-semibold text-lg text-white">
                                {getCategoryLabel(category).toUpperCase()}
                              </td>
                            </tr>
                            {categoryProducts.map((product) => (
                              <motion.tr
                                key={product.id}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className={`border-b-2 border-black ${!product.active ? 'opacity-50' : ''} ${
                                  product.category === 'produce' ? 'bg-green-50 hover:bg-green-100' :
                                  product.category === 'meat-beef' ? 'bg-red-50 hover:bg-red-100' :
                                  product.category === 'meat-chicken' ? 'bg-orange-50 hover:bg-orange-100' :
                                  product.category === 'meat-pork' ? 'bg-pink-50 hover:bg-pink-100' :
                                  product.category === 'meat-hotpot' ? 'bg-rose-50 hover:bg-rose-100' :
                                  product.category === 'seafood' ? 'bg-blue-50 hover:bg-blue-100' :
                                  product.category === 'groceries' ? 'bg-amber-50 hover:bg-amber-100' :
                                  'bg-white hover:bg-gray-50'
                                }`}
                              >
                          {canReorder && (
                            <td className="px-4 py-3">
                              <div className="flex gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleMoveProduct(product.id, 'up')}
                                  disabled={processedProducts.findIndex(p => p.id === product.id) === 0}
                                  className="rounded-lg p-3 bg-purple-700 hover:bg-purple-800 border-purple-700 hover:border-purple-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <ArrowUp className="h-6 w-6 text-white" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleMoveProduct(product.id, 'down')}
                                  disabled={processedProducts.findIndex(p => p.id === product.id) === processedProducts.length - 1}
                                  className="rounded-lg p-3 bg-purple-700 hover:bg-purple-800 border-purple-700 hover:border-purple-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <ArrowDown className="h-6 w-6 text-white" />
                                </Button>
                              </div>
                            </td>
                          )}
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded bg-gray-100 overflow-hidden flex-shrink-0">
                                {product.imageUrl ? (
                                  <img 
                                    src={product.imageUrl} 
                                    alt={product.description}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Package className="w-5 h-5 text-gray-400" />
                                  </div>
                                )}
                              </div>
                              <div>
                                <span className="font-medium">{product.description}</span>
                                {product.chineseName && (
                                  <span className="text-gray-600 text-sm block">{product.chineseName}</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={getCategoryColor(product.category, true)}>
                              {getCategoryLabel(product.category)}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{product.size}</td>
                          <td className="px-4 py-3 text-red-500 line-through">
                            {formatPrice(product.retailPrice)}
                          </td>
                          <td className="px-4 py-3 font-semibold text-emerald-600">
                            {formatPrice(product.ourPrice)}
                          </td>
                          <td className="px-4 py-3">
                            <Badge className="bg-emerald-100 text-emerald-800">
                              {calculateSavingsPercentage(product.retailPrice, product.ourPrice)}%
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <Switch 
                              checked={product.active} 
                              onCheckedChange={(checked) => updateProductMutation.mutate({ id: product.id, updates: { active: checked } })}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => setEditingProduct(product)}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => setProductToDelete({ id: product.id, name: product.description })}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </motion.tr>
                            ))}
                          </React.Fragment>
                        )
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit Product Modal */}
      <EditProductModal
        product={editingProduct}
        isOpen={!!editingProduct}
        onClose={() => setEditingProduct(null)}
        onSave={(id, updates) => updateProductMutation.mutate({ id, updates })}
      />

      {/* Add Product Modal */}
      <AddProductModal
        isOpen={showAddForm}
        onClose={() => setShowAddForm(false)}
        onSave={(product) => createProductMutation.mutate(product)}
      />

      {/* User Management Modal */}
      {showUserManagement && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold">User Management</h2>
              <Button variant="outline" size="sm" onClick={() => setShowUserManagement(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-8rem)]">
              <UserManagement />
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!productToDelete} onOpenChange={(open) => !open && setProductToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Product</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{productToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}